import type { MessageKey } from '@freightapi/i18n'
import { Button, Spinner } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DeliveryFlow } from '../components/delivery/DeliveryFlow'
import { QueuedNotice } from '../components/queue/QueuedNotice'
import { StatusScreen } from '../components/StatusScreen'
import { IdentificationPanel } from '../components/sign/IdentificationPanel'
import { OtpPanel, type OtpSigner } from '../components/sign/OtpPanel'
import { SignForm } from '../components/sign/SignForm'
import {
  offersClickOtp,
  type SignMethod,
  SignMethodChoice,
} from '../components/sign/SignMethodChoice'
import { SignSuccess } from '../components/sign/SignSuccess'
import { SignSummary } from '../components/sign/SignSummary'
import { ApiError } from '../lib/api'
import { isDeliveryFlow } from '../lib/delivery'
import { type SignLocale, useSignI18n } from '../lib/locale'
import { newQueueGroup, queueSignature } from '../lib/offline-queue'
import type { QueueItem } from '../lib/queue-store'
import {
  type DrawnSubmission,
  loadSignContext,
  type OtpChallenge,
  type OtpChannel,
  requestSignOtp,
  type SignatureResource,
  type SignContext,
  submitSignature,
  verifySignOtp,
} from '../lib/sign'

/** The signing link itself: `SIGN_BASE_URL/s/<token>` (M2-03). */
export const Route = createFileRoute('/s/$token')({ component: SignScreen })

/**
 * The states the ticket names, as one discriminated union. A sign link is opened once, on a phone,
 * often on a bad connection, so every outcome is a screen that says what to do next rather than a
 * banner over a half-loaded page.
 *
 * Nothing in here holds translated text: a state carries a message *key*, so switching language
 * re-renders the current screen instead of reloading a link that may already have been used.
 */
type FlowState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'expired' }
  | { readonly kind: 'revoked' }
  | { readonly kind: 'used' }
  | { readonly kind: 'not_found' }
  | {
      readonly kind: 'error'
      readonly messageKey: MessageKey<'sign'>
      readonly retryable: boolean
    }
  | {
      readonly kind: 'ready'
      readonly context: SignContext
      /**
       * `method` only appears when there is something to choose (M2-04): a link that allows
       * `click_otp` and names somewhere to send a code to. Otherwise `review` leads straight to
       * the pad, exactly as it did before click-to-sign existed. `identification` only appears on
       * a link that asks for `ades` or `qes` (M5-02).
       */
      readonly step: 'review' | 'identification' | 'method' | 'sign' | 'otp'
    }
  | { readonly kind: 'submitting'; readonly context: SignContext }
  | {
      readonly kind: 'success'
      readonly context: SignContext
      readonly signature: SignatureResource
    }
  /** Drawn without a connection: the queue of M2-08 has it and will deliver it. */
  | { readonly kind: 'queued'; readonly item: QueueItem }

function SignScreen() {
  const { token } = Route.useParams()
  const { t, locale } = useSignI18n()
  const [state, setState] = useState<FlowState>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)
  /** This visit, for the queue: one signature, but the same bookkeeping as an errand with photos. */
  const [group] = useState(newQueueGroup)

  /**
   * The one-time password this session is working on (M2-04). It lives here rather than in the
   * panel because it outlives the screen that made it: going back to correct a name must not throw
   * away a code that is already in somebody's inbox, and asking for a second one would spend one
   * of the five the API hands out per ten minutes.
   */
  const [challenge, setChallenge] = useState<OtpChallenge | undefined>(undefined)

  /**
   * The proof that the signer identified themselves at the trust service provider (M5-02). Like
   * the one-time password above it outlives the screen that earned it, so going back to correct a
   * name does not send somebody through an eID flow a second time. It is single use and lives
   * fifteen minutes, which is why a signature that needs one never goes through the offline queue.
   */
  const [identificationToken, setIdentificationToken] = useState<string | undefined>(undefined)

  // Only the `Accept-Language` of the next request, never a reason to fetch again: the API answers
  // the same consignment note in every language and the screens translate themselves.
  const localeRef = useRef(locale)
  localeRef.current = locale

  /**
   * The token the signature goes out with. Normally the one in the URL — but a handoff token is
   * burned by the call that opened this page, and the context then names the sign token it
   * resolved to, which is the one the new device has to submit with (M1-10, M2-06).
   */
  const signTokenRef = useRef(token)

  // `attempt` is what "retry" increments: re-running the effect, with a key that has not been asked
  // for before, is the entire point of the dependency.
  useEffect(() => {
    let active = true
    setState({ kind: 'loading' })

    resolveOnce(token, attempt, localeRef.current)
      .then((context) => {
        if (!active) return
        signTokenRef.current = context.token ?? token
        setState({ kind: 'ready', context, step: 'review' })
      })
      .catch((cause: unknown) => {
        if (!active) return
        setState(failureState(cause))
      })

    return () => {
      active = false
    }
  }, [token, attempt])

  const retry = useCallback(() => setAttempt((value) => value + 1), [])

  /**
   * Maps a failed call onto what the signer should read. A link that expired, was already used or
   * never existed replaces the screen — there is nothing to retype. Everything else stays on the
   * code screen as one line under the field, because the next try is the signer's to make.
   */
  const otpFailure = useCallback(
    (cause: unknown): string | undefined => {
      const failure = failureState(cause)
      if (failure.kind !== 'error') {
        setState(failure)
        return undefined
      }
      if (!(cause instanceof ApiError)) return t('error.unexpected')
      return t(OTP_MESSAGE_KEY_BY_CODE[cause.code] ?? failure.messageKey)
    },
    [t],
  )

  const requestCode = useCallback(
    async (channel: OtpChannel): Promise<string | undefined> => {
      try {
        setChallenge(
          await requestSignOtp(signTokenRef.current, channel, { locale: localeRef.current }),
        )
        return undefined
      } catch (cause) {
        return otpFailure(cause)
      }
    },
    [otpFailure],
  )

  /**
   * The confirm button: the six digits become a single-use proof, and that proof signs. Two calls
   * behind one tap, because they are one action for the signer and because a proof lives five
   * minutes — holding one while a second screen is filled in would only give it time to expire.
   *
   * This path deliberately does not go through the offline queue of M2-08. A code cannot be asked
   * for or checked without a connection in the first place, and a proof that waits out a tunnel is
   * a proof that expires on the way: failing here and now, with a message the signer can act on,
   * beats a queued signature that is refused hours later with `otp_invalid`.
   */
  const confirmWithCode = useCallback(
    async (input: {
      readonly code: string
      readonly channel: OtpChannel
      readonly signer: OtpSigner
    }): Promise<string | undefined> => {
      try {
        const verified = await verifySignOtp(
          signTokenRef.current,
          { code: input.code, channel: input.channel },
          { locale: localeRef.current },
        )
        const signature = await submitSignature(
          signTokenRef.current,
          {
            method: 'click_otp',
            signer: input.signer,
            otp_verified_token: verified.otp_verified_token,
            otp_channel: input.channel,
            consent: true,
            ...(identificationToken === undefined
              ? {}
              : { identification_token: identificationToken }),
          },
          { locale: localeRef.current },
        )
        setState((current) =>
          current.kind === 'ready'
            ? { kind: 'success', context: current.context, signature }
            : current,
        )
        return undefined
      } catch (cause) {
        return otpFailure(cause)
      }
    },
    [otpFailure, identificationToken],
  )

  /** What a sub-flow reports when it hits something it cannot recover from. */
  const fail = useCallback((cause: unknown) => setState(failureState(cause)), [])

  /**
   * A drawn signature always goes through the queue (M2-08); click-to-sign does not, for the
   * reason above. With a connection this is the same single call it was before and the signer sees
   * the same confirmation; without one the drawing is kept on the device and the confirmation says
   * so, instead of an error that loses it.
   */
  const submit = useCallback(
    (drawn: DrawnSubmission) => {
      const body: DrawnSubmission =
        identificationToken === undefined
          ? drawn
          : { ...drawn, identification_token: identificationToken }
      setState((current) =>
        current.kind === 'ready' ? { kind: 'submitting', context: current.context } : current,
      )
      // An `ades` or `qes` signature does not go through the queue of M2-08 (M5-02). The proof of
      // identification lives fifteen minutes and is single use: one that waits out a tunnel is one
      // that is refused hours later, and a driver who is told "sent" would find out from the
      // queue screen rather than from the person still standing at the tailgate.
      const sending =
        identificationToken === undefined
          ? queueSignature({ token: signTokenRef.current, group, body })
          : submitSignature(signTokenRef.current, body, { locale: localeRef.current }).then(
              (result) => ({ state: 'sent', result }) as const,
            )
      sending
        .then((sent) => {
          setState((current) => {
            if (current.kind !== 'submitting') return current
            return sent.state === 'sent'
              ? { kind: 'success', context: current.context, signature: sent.result }
              : { kind: 'queued', item: sent.item }
          })
        })
        // Only a refusal reaches here; anything the queue can still deliver was resolved above.
        .catch((cause: unknown) => setState(failureState(cause)))
    },
    [group, identificationToken],
  )

  /** Which of the `ready` screens is on, and where each of their buttons leads. */
  function renderReady(ready: Extract<FlowState, { kind: 'ready' }>) {
    const choice = offersClickOtp(ready.context)
    const chooseMethod = (method: SignMethod) =>
      setState({ ...ready, step: method === 'drawn' ? 'sign' : 'otp' })
    // The identification comes between reviewing the note and signing it (M5-02): the signer has
    // seen what they are about to put their name under, and the provider authorises exactly that
    // hash. Once it is done the step is skipped, so correcting a name does not repeat an eID flow.
    const identification = ready.context.identification
    const afterReview: Extract<FlowState, { kind: 'ready' }>['step'] =
      identification !== undefined && identificationToken === undefined
        ? 'identification'
        : choice
          ? 'method'
          : 'sign'

    switch (ready.step) {
      case 'review':
        return (
          <SignSummary
            context={ready.context}
            onContinue={() => setState({ ...ready, step: afterReview })}
          />
        )

      case 'identification':
        if (identification === undefined) {
          return (
            <SignSummary
              context={ready.context}
              onContinue={() => setState({ ...ready, step: afterReview })}
            />
          )
        }
        return (
          <IdentificationPanel
            step={identification}
            token={signTokenRef.current}
            {...(ready.context.recipient_name === undefined
              ? {}
              : { recipientName: ready.context.recipient_name })}
            onVerified={(proof) => {
              setIdentificationToken(proof)
              setState({ ...ready, step: choice ? 'method' : 'sign' })
            }}
            onBack={() => setState({ ...ready, step: 'review' })}
          />
        )

      case 'method':
        return (
          <SignMethodChoice
            context={ready.context}
            onChoose={chooseMethod}
            onBack={() => setState({ ...ready, step: 'review' })}
          />
        )

      case 'otp':
        return (
          <OtpPanel
            context={ready.context}
            challenge={challenge}
            onRequestCode={requestCode}
            onConfirm={confirmWithCode}
            onBack={() => setState({ ...ready, step: 'method' })}
          />
        )

      case 'sign':
        return (
          // Rendered at the same position as the `submitting` branch below, so React keeps the
          // form state — and therefore the drawn signature — across the round trip.
          <SignForm
            context={ready.context}
            busy={false}
            onBack={() => setState({ ...ready, step: choice ? 'method' : 'review' })}
            onSubmit={submit}
          />
        )
    }
  }

  switch (state.kind) {
    case 'loading':
      return (
        <div className="flex justify-center py-16">
          <Spinner label={t('loading')} size="lg" />
        </div>
      )

    case 'expired':
      return (
        <StatusScreen
          tone="warn"
          title={t('status.expired.title')}
          body={t('status.expired.body')}
        />
      )

    case 'revoked':
      return (
        <StatusScreen
          tone="warn"
          title={t('status.revoked.title')}
          body={t('status.revoked.body')}
        />
      )

    case 'used':
      return (
        <StatusScreen tone="warn" title={t('status.used.title')} body={t('status.used.body')} />
      )

    case 'not_found':
      return (
        <StatusScreen
          tone="danger"
          title={t('error.not_found.title')}
          body={t('error.not_found.body')}
        />
      )

    case 'error':
      return (
        <StatusScreen tone="danger" title={t('error.title')} body={t(state.messageKey)}>
          {state.retryable ? (
            <Button size="lg" onClick={retry}>
              {t('button.retry')}
            </Button>
          ) : null}
        </StatusScreen>
      )

    case 'ready':
      // Box 24 on a consignment note that is under way is not one signature but an errand: count
      // the packages, record what is wrong with them, hand the phone over, sign, take the proof
      // along (M2-05). It owns its own steps, so the route hands it the link and steps aside.
      if (isDeliveryFlow(state.context)) {
        return (
          <DeliveryFlow
            context={state.context}
            token={signTokenRef.current}
            handedOff={state.context.token !== undefined}
            onFailure={fail}
          />
        )
      }
      return renderReady(state)

    case 'submitting':
      return (
        <SignForm
          context={state.context}
          busy
          onBack={() => setState({ kind: 'ready', context: state.context, step: 'review' })}
          onSubmit={submit}
        />
      )

    case 'success':
      return <SignSuccess context={state.context} signature={state.signature} />

    case 'queued':
      return <QueuedNotice item={state.item} />
  }
}

/**
 * The contexts this page has asked for, keyed by link and attempt.
 *
 * Opening a link resolves it, and resolving a handoff token *spends* it: the second device gets
 * one call and one only (M1-10). React mounts a component twice in development, and a browser may
 * re-mount a route for reasons of its own, so the request is remembered rather than repeated —
 * otherwise the phone that just scanned the QR code would show "link expired" to the person
 * holding it. A failure is forgotten again, so the retry button and a later visit are real calls.
 */
const resolving = new Map<string, Promise<SignContext>>()

function resolveOnce(token: string, attempt: number, locale: SignLocale): Promise<SignContext> {
  const key = `${attempt}\u0000${token}`
  const pending = resolving.get(key)
  if (pending !== undefined) return pending

  const request = loadSignContext(token, { locale }).catch((cause: unknown) => {
    resolving.delete(key)
    throw cause
  })
  resolving.set(key, request)
  return request
}

/**
 * What the code screen says for a failure it can stay on. The three the ticket names are the ones a
 * signer can act on; a refused send and a spent issue window get their own line too, because "try
 * again" is wrong advice when the API is asking for ten minutes of patience.
 */
const OTP_MESSAGE_KEY_BY_CODE: Partial<Record<string, MessageKey<'sign'>>> = {
  otp_invalid: 'sign.otp.error.invalid',
  otp_locked: 'sign.otp.error.locked',
  rate_limited: 'sign.otp.error.rate_limited',
  service_unavailable: 'sign.otp.error.send',
  validation_failed: 'sign.otp.error.unavailable',
}

/** Maps an API failure onto the screen it deserves; anything unknown is the generic error. */
function failureState(cause: unknown): FlowState {
  if (!(cause instanceof ApiError)) {
    return { kind: 'error', messageKey: 'error.unexpected', retryable: false }
  }
  switch (cause.code) {
    case 'token_revoked':
      return { kind: 'revoked' }
    case 'token_expired':
    case 'link_expired':
    case 'token_rotated':
      return { kind: 'expired' }
    case 'already_signed':
      return { kind: 'used' }
    case 'not_found':
      return { kind: 'not_found' }
    default:
      return { kind: 'error', messageKey: cause.messageKey, retryable: cause.isRetryable }
  }
}
